import React, { useMemo } from 'react';
import { arrayOf, func, string } from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';

import { useConfiguration } from '../../../context/configurationContext';
import {
  LISTING_FILTERS_CONFIG,
  TYPES_EXCLUDED_FROM_FILTERS,
} from '../../../constants/listing.constants';
import { parse } from '../../../util/urlHelpers';
import { TAXONOMY_SEPARATOR } from '../../../util/listingTaxonomy';
import {
  configsByKey,
  getDropdownOptions,
  SEARCH_PARAM_NAME_CATEGORY,
  SEARCH_PARAM_NAME_TYPE,
} from '../filters/CategoryFilter';
import { navigateWithCategory } from '../DropdownFilterEnum/navigateWithCategory';
import NestedCategoryDropdown from './NestedCategoryDropdown';
import {
  getCategoryState,
  getPrimaryCategory,
  getSelectionCount,
  readSelection,
  toggleCategory,
  toggleType,
} from './categoryTypeSelection';

const isExcludedFromFilters = (category, type) =>
  TYPES_EXCLUDED_FROM_FILTERS.some(t => t.category === category && t.type === type);

/** The types the filter offers under a category, as `category/type` URL values. */
const getFilterTypes = (category, intl) =>
  (LISTING_FILTERS_CONFIG[category] || [])
    .filter(type => !isExcludedFromFilters(category, type))
    .map(type => ({
      value: `${category}${TAXONOMY_SEPARATOR}${type}`,
      label: intl.formatMessage({ id: `General.listing.type.${category}.${type}` }),
    }));

/**
 * The search page's category filter, with each category's types one level
 * down. Visitors pick whole categories, category/type pairs, or a mix; the URL
 * keeps its `category=` / `type=category/type` shape, and SearchPage.duck turns
 * that into the query (util/listingTaxonomy.js).
 */
const CategoryTypeFilter = props => {
  const { categoryOptions, onToggle, variant } = props;

  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const { search, pathname, state } = location;

  const categoryConfig = configsByKey(config?.listing?.listingFields)?.category;

  const categories = useMemo(
    () =>
      (getDropdownOptions(categoryConfig, categoryOptions, intl) || []).map(option => ({
        value: option.value,
        label: option.label,
        group: option.group,
        types: getFilterTypes(option.value, intl),
      })),
    [categoryConfig, categoryOptions, intl]
  );

  const parsed = parse(search);
  const selection = readSelection({
    category: parsed[SEARCH_PARAM_NAME_CATEGORY],
    type: parsed[SEARCH_PARAM_NAME_TYPE],
  });

  const applySelection = next => {
    const {
      page,
      [SEARCH_PARAM_NAME_CATEGORY]: _category,
      [SEARCH_PARAM_NAME_TYPE]: _type,
      ...rest
    } = parse(search);

    navigateWithCategory({
      history,
      match,
      pathname,
      state,
      params: {
        ...rest,
        [SEARCH_PARAM_NAME_CATEGORY]: next.categories,
        [SEARCH_PARAM_NAME_TYPE]: next.types,
      },
      selectedCategory: getPrimaryCategory(next),
    });
  };

  return (
    <NestedCategoryDropdown
      categories={categories}
      placeholder={intl.formatMessage({ id: 'Global.category' })}
      selectionCount={getSelectionCount(selection)}
      getCategoryState={category => getCategoryState(selection, category)}
      isTypeSelected={typeValue => selection.types.includes(typeValue)}
      onCategoryClick={category => applySelection(toggleCategory(selection, category))}
      onTypeClick={typeValue => applySelection(toggleType(selection, typeValue))}
      onToggle={onToggle}
      variant={variant}
    />
  );
};

CategoryTypeFilter.propTypes = {
  categoryOptions: arrayOf(string).isRequired,
  onToggle: func,
  variant: string,
};

export default CategoryTypeFilter;
